// Type-only declarations. The matrix lives ONLY on the backend (Pillar 5 — ZERO frontend logic).
// React fetches /me/capabilities and trusts whatever permissions the server returns.
// This file gives IDE autocomplete on permission names; it never decides whether you have one.

export const PermissionName = {
  ORDER_CREATE: 'ORDER_CREATE',
  ORDER_VIEW_ALL: 'ORDER_VIEW_ALL',
  ORDER_VIEW_ALL_TODAY: 'ORDER_VIEW_ALL_TODAY',
  ORDER_VIEW_OWN_COMPANY: 'ORDER_VIEW_OWN_COMPANY',
  ORDER_CONFIRM: 'ORDER_CONFIRM',
  ORDER_PAY: 'ORDER_PAY',
  // A B2B buyer paying its own comptant order by card online (CAWL).
  ORDER_PAY_ONLINE: 'ORDER_PAY_ONLINE',
  ORDER_CANCEL: 'ORDER_CANCEL',
  ORDER_DELIVER: 'ORDER_DELIVER',
  ORDER_PREPARE: 'ORDER_PREPARE',
  ORDER_READY: 'ORDER_READY',
  ORDER_APPROVE_CREDIT: 'ORDER_APPROVE_CREDIT',
  QUOTE_CREATE: 'QUOTE_CREATE',
  STOCK_VIEW_QTY: 'STOCK_VIEW_QTY',
  STOCK_RECEIVE: 'STOCK_RECEIVE',
  B2B_USER_CREATE: 'B2B_USER_CREATE',
  B2B_USER_VIEW: 'B2B_USER_VIEW',
  STAFF_USER_VIEW: 'STAFF_USER_VIEW',
  STAFF_USER_MANAGE: 'STAFF_USER_MANAGE',
  SUPPLIER_COMPARE: 'SUPPLIER_COMPARE',
  SUPPLIER_COMPARE_VIEW: 'SUPPLIER_COMPARE_VIEW',
  PO_PLACE: 'PO_PLACE',
  // WP-4: PO_PLACE creates/transmits a supplier order, PO_APPROVE clears the PENDING_APPROVAL
  // gate on a manual restocking PO (MANAGER/OWNER — never the PURCHASER who raised it).
  PO_APPROVE: 'PO_APPROVE',
  SUPPLIER_INVOICE_OCR: 'SUPPLIER_INVOICE_OCR',
  SUPPLIER_RETURN_CREATE: 'SUPPLIER_RETURN_CREATE',
  DISCOUNT_REQUEST: 'DISCOUNT_REQUEST',
  DISCOUNT_APPROVE_LE_15: 'DISCOUNT_APPROVE_LE_15',
  DISCOUNT_APPROVE_UNLIMITED: 'DISCOUNT_APPROVE_UNLIMITED',
  DISCOUNT_APPLY_DIRECT: 'DISCOUNT_APPLY_DIRECT',
  // Counter authority: autonomous discount down to the cost floor (counterMaxDiscountPct ships
  // at 100 %, i.e. no ceiling; an admin may set one, and above it an on-screen manager override
  // is needed). Never below cost — no override lifts that.
  DISCOUNT_APPLY_CAPPED: 'DISCOUNT_APPLY_CAPPED',
  // May ring a sale UNDER purchase cost (OWNER/ADMIN/MANAGER), doing it themselves.
  MARGIN_SELL_BELOW_COST: 'MARGIN_SELL_BELOW_COST',
  // Create a DRAFT catalogue product from a live supplier quote, at the counter. Does NOT carry
  // the authority to set a cost — the server re-fetches it from the supplier.
  PRODUCT_CREATE_FROM_OFFER: 'PRODUCT_CREATE_FROM_OFFER',
  // Sees purchase cost + margin (% and €) on a line — decoupled from pricing authority.
  MARGIN_VIEW: 'MARGIN_VIEW',
  PRICING_GRIDS_RW: 'PRICING_GRIDS_RW',
  SYSTEM_SETTINGS_RW: 'SYSTEM_SETTINGS_RW',
  INVOICE_VIEW_ALL: 'INVOICE_VIEW_ALL',
  INVOICE_VIEW_OWN_COMPANY: 'INVOICE_VIEW_OWN_COMPANY',
  CREDIT_NOTE_CREATE: 'CREDIT_NOTE_CREATE',
  DELIVERY_NOTE_VIEW: 'DELIVERY_NOTE_VIEW',
  ACCOUNTING_EXPORT: 'ACCOUNTING_EXPORT',
  PAYMENT_RECONCILE: 'PAYMENT_RECONCILE',
  POS_SESSION_OPEN: 'POS_SESSION_OPEN',
  POS_SESSION_CLOSE: 'POS_SESSION_CLOSE',
  POS_SESSION_VALIDATE: 'POS_SESSION_VALIDATE',
  CUSTOMER_RETURN_APPROVE: 'CUSTOMER_RETURN_APPROVE',
  AUDIT_VIEW: 'AUDIT_VIEW',
  CATALOG_WRITE: 'CATALOG_WRITE',
  PRODUCT_COST_WRITE: 'PRODUCT_COST_WRITE',
  SALES_SEARCH: 'SALES_SEARCH',
} as const;

export type Permission = (typeof PermissionName)[keyof typeof PermissionName];

// Backwards-compat alias so existing imports keep working.
export const Permission = PermissionName;
